import { alpha, CheckboxProps, TableRow } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { BlastRadiusCell } from "./BlastRadiusCell";
import { CheckCell } from "./CheckCell";
import { AdvisoryCell } from "./AdvisoryCell";
import { FirstFoundCell } from "./FirstFoundCell";
import { VulnerabilityCell } from "./VulnerabilityCell";
import {
  AdjustedAggregateScoreType,
  AggregateScoresNode,
} from "../FirewatchPage";
import { AggregateScoreType, ScoreRating } from "~/operations";
import { Header } from "../FirewatchPage/FirewatchPageColumns";
import { DateCell } from "./DateCell";
import { RiskValueCell } from "./RiskValueCell";
import { PolicyNameCell } from "./PolicyNameCell";
import { AssetCell } from "./AssetCell";
import { SoftwareCell } from "~/components/FirewatchRow/SoftwareCell";
import { AssetsCountCell } from "~/components/FirewatchRow/AssetsCountCell";
import { CheckboxCell, ImpactCell, RiskFactorsCell } from "../Cells";
import { SpaceOrWorkspaceScope } from "~/hooks/useScope";
import { getLinkByType } from "~/components/FirewatchPage/lib/getLinkByType";
import { Space } from "~/lib/types";
import { TopSecurityFindingCell } from "./TopSecurityFindingCell";
import { AllSecurityFindingCell } from "./AllSecurityFindingCell";
import { SwitchBaseProps } from "@mui/material/internal/SwitchBase";
import { useFirewatchException } from "~/components/FirewatchRow/hooks/useFirewatchException";
import { FindingTableTriageCell } from "~/pages/inventory/components/Findings/FindingsTable/FindingTableTriageCell";
import TopActionsCardSpaceRiskCell from "~/components/FirewatchRow/TopActionsCardSpaceRiskCell";
import TopActionsCardEffortCell from "~/components/FirewatchRow/TopActionsCardEffortCell";
import { TopActionFindingCell } from "./TopActionFindingCell";
import { TopActionsAssetsCountCell } from "./TopActionsAssetsCountCell";
import { RankCell } from "~/components/DashboardList";
import { ActionCell } from "./ActionCell";
import { SlaCell } from "./SlaCell";
import { PreviewStatusCell } from "../Cells/PreviewStatusCell";

const isLink = (pageType: AdjustedAggregateScoreType): boolean => {
  // If the page type is Vulnerability or Advisory, we should link to the specific page
  const shouldLinkTypes = [
    AggregateScoreType.Policy,
    AggregateScoreType.Vulnerability,
    AggregateScoreType.Advisory,
    AggregateScoreType.Asset,
    "NewestRelevantVulnerabilities",
    "AllSecurityFindings",
    "AssetAllFindings",
    "TopSecurityFindings",
    "TopActionsRiskReduction",
    "SlaFindings",
    AggregateScoreType.Check,
    AggregateScoreType.Software,
    AggregateScoreType.VersionedSoftware,
  ];

  return shouldLinkTypes.includes(pageType);
};

type FirewatchRowProps = {
  pageType: AdjustedAggregateScoreType;
  tableHeaders: Header[];
  row: AggregateScoresNode;
  scope?: SpaceOrWorkspaceScope;
  space: Space;
  size?: "small" | "list";
  noTags?: boolean;
  queryParams?: Record<string, string>;
  selectionProps?: {
    canSelect: boolean;
    isSelected: boolean;
    onCheckboxSelect: SwitchBaseProps["onChange"];
    onCheckboxClick: CheckboxProps["onClick"];
  };
};

export function FirewatchRow({
  pageType,
  tableHeaders,
  row,
  space,
  scope,
  size,
  noTags,
  queryParams: queryParamsFromProps,
  selectionProps,
}: FirewatchRowProps) {
  const isRowLink = isLink(pageType);
  const navigate = useNavigate();
  const {
    hasException,
    isRiskAccepted,
    isDisabled,
    isWorkaround,
    isFalsePositive,
    exceptionTooltipProps,
  } = useFirewatchException({
    row,
  });

  const TITLE_MAX_WIDTH = pageType === "SlaFindings" ? 500 : 600;
  const isSmallTable = size === "small";

  const handleRowClick = () => {
    if (isRowLink && row?.findingMrn) {
      const queryParams =
        scope?.params || new URLSearchParams({ spaceId: space.id });

      const queryParamsCopy = new URLSearchParams(queryParams);
      if (queryParamsFromProps) {
        Object.entries(queryParamsFromProps).forEach(([key, value]) => {
          queryParamsCopy.set(key, value);
        });
      }

      navigate(
        getLinkByType({
          type: pageType,
          findingMrn: row.findingMrn,
          scoreType: row.scoreType,
          entity:
            row.entity?.__typename === "EntityInfoAsset"
              ? row?.entity
              : undefined,
          queryParams: queryParamsCopy,
        }),
      );
    }
  };

  return (
    <TableRow
      data-testid="firewatch-list-row"
      onClick={handleRowClick}
      sx={{
        "&.MuiTableRow-root:hover .firewatch-chip": {
          backgroundColor: "background.lighter",
        },
        "&.MuiTableRow-root:hover": {
          backgroundColor: (theme) =>
            `${alpha(theme.palette.background.light, 0.6)}!important`,
        },
        ...(isSmallTable && {
          sx: { td: { pl: 0 } },
        }),
        ...(isRowLink && {
          cursor: "pointer",
          "&:hover": {
            backgroundColor: "action.hover",
          },
        }),
      }}
    >
      {tableHeaders.map((header) => {
        if (header.id === "SELECT" && selectionProps?.canSelect) {
          return (
            <CheckboxCell
              key={`${header.id}-${row?.id}`}
              onClick={selectionProps?.onCheckboxClick}
              onChange={selectionProps?.onCheckboxSelect}
              checked={Boolean(selectionProps?.isSelected)}
            />
          );
        } else if (header.id === "RANK") {
          return (
            <RankCell
              key={`${header.id}-${row?.id}`}
              isSmall={isSmallTable}
              id={row?.rank || 0}
            />
          );
        } else if (header.id === "IMPACT") {
          return (
            <ImpactCell
              key={`${header.id}-${row?.id}`}
              impact={row?.riskValue || 0}
              rating={row?.rating || ScoreRating.Unscored}
              isActive={!hasException}
            />
          );
        } else if (header.id === "BLAST_RADIUS") {
          return (
            <BlastRadiusCell
              key={`${header.id}-${row?.id}`}
              blastRadius={row?.blastRadius}
              isActive={!hasException}
              rating={row?.rating || ScoreRating.Unscored}
            />
          );
        } else if (header.id === "CHECK") {
          return (
            <CheckCell
              key={`${header.id}-${row?.id}`}
              row={row}
              isSmallTable={isSmallTable}
              noTags={noTags}
              maxWidth={500}
            />
          );
        } else if (header.id === "ALL_SECURITY_FINDING") {
          return (
            <AllSecurityFindingCell
              key={`${header.id}-${row?.id}`}
              type={row.scoreType}
              CheckCellProps={{
                row,
                isSmallTable,
                noTags,
                maxWidth: TITLE_MAX_WIDTH,
              }}
              VulnerabilityCellProps={{ row, size, maxWidth: TITLE_MAX_WIDTH }}
              AdvisoryCellProps={{ row, maxWidth: TITLE_MAX_WIDTH }}
            />
          );
        } else if (header.id === "TOP_SECURITY_FINDING") {
          return (
            <TopSecurityFindingCell
              key={`${header.id}-${row?.id}`}
              type={row.scoreType}
              title={row.title}
              description={row.description}
              size={size}
            />
          );
        } else if (header.id === "ADVISORY") {
          return (
            <AdvisoryCell
              key={`${header.id}-${row?.id}`}
              row={row}
              maxWidth={TITLE_MAX_WIDTH}
            />
          );
        } else if (header.id === "VULNERABILITY") {
          return (
            <VulnerabilityCell
              key={`${header.id}-${row?.id}`}
              row={row}
              size={size}
              maxWidth={TITLE_MAX_WIDTH}
            />
          );
        } else if (header.id === "ASSET") {
          return (
            <AssetCell
              key={`${header.id}-${row?.id}`}
              {...{
                title: row.title,
                tags: row.tags,
                iconId: row.iconId,
                isSnoozed: isRiskAccepted,
                isDisabled,
                isFalsePositive,
                isWorkaround,
                exceptionTooltipProps,
              }}
            />
          );
        } else if (header.id === "POLICY") {
          return <PolicyNameCell key={`${header.id}-${row?.id}`} row={row} />;
        } else if (header.id === "SOFTWARE") {
          return <SoftwareCell key={`${header.id}-${row?.id}`} row={row} />;
        } else if (header.id === "STATUS") {
          return (
            <PreviewStatusCell
              key={`${header.id}-${row?.id}`}
              enforcementState={row.enforcementState}
            />
          );
        } else if (header.id === "RISK_FACTORS") {
          return (
            <RiskFactorsCell
              key={`${header.id}-${row?.id}`}
              riskFactors={row?.riskFactors}
              isActive={!hasException}
            />
          );
        } else if (header.id === "RISK_VALUE") {
          return (
            <RiskValueCell
              key={`${header.id}-${row?.id}`}
              riskValue={row?.riskValue || 0}
            />
          );
        } else if (header.id === "DATE") {
          return (
            <DateCell
              key={`${header.id}-${row?.id}`}
              date={row?.firstDetectedAt}
            />
          );
        } else if (header.id === "LAST_UPDATED") {
          // TODO: Add update to mesh with first found cell
          return (
            <FirstFoundCell
              key={`${header.id}-${row?.id}`}
              discoveryTime={row?.lastScannedAt}
            />
          );
        } else if (header.id === "ASSETS_COUNT") {
          return (
            <AssetsCountCell
              key={`${header.id}-${row?.id}`}
              count={row?.blastRadius?.assets || 0}
            />
          );
        } else if (header.id === "TOP_ACTIONS_ASSETS_COUNT") {
          return (
            <TopActionsAssetsCountCell
              key={`${header.id}-${row?.id}`}
              count={row?.blastRadius?.affected || 0}
              isSmallTable={isSmallTable}
            />
          );
        } else if (header.id === "TOP_ACTIONS_FINDING") {
          return (
            <TopActionFindingCell
              type={row.scoreType}
              checkCellProps={{ row, isSmallTable, noTags }}
              advisoryCellProps={{ row, isSmallTable: isSmallTable }}
              vulnerabilityCellProps={{ row, size }}
            />
          );
        } else if (header.id === "TRIAGE_BAR") {
          return (
            <FindingTableTriageCell
              key={`${header.id}-${row?.id}`}
              finding={row}
              type="small-table"
            />
          );
        } else if (header.id === "SPACE_RISK") {
          return (
            <TopActionsCardSpaceRiskCell
              spaceRiskImprovement={row.spaceRiskImprovement}
            />
          );
        } else if (header.id === "EFFORT") {
          return (
            <TopActionsCardEffortCell
              effort={row.effort}
              manualEffort={row?.manualEffort}
            />
          );
        } else if (header.id === "ACTION") {
          return <ActionCell key={`${header.id}-${row?.id}`} row={row} />;
        } else if (header.id === "SLA_OVERDUE_COUNT") {
          return (
            <SlaCell
              key={`${header.id}-${row?.id}`}
              value={row.slaOverdueCount}
              type="Overdue"
            />
          );
        } else if (header.id === "SLA_AT_RISK_COUNT") {
          return (
            <SlaCell
              key={`${header.id}-${row?.id}`}
              value={row.slaAtRiskCount}
              type="AtRisk"
            />
          );
        }
        return <div></div>;
      })}
    </TableRow>
  );
}
